import breakpoints from 'utils/breakpoints';

/**
 * Component for the Site Header.
 */
export default class SiteHeader {
  /**
   * Start the component
   */
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    // Bind methods
    this.init = this.init.bind(this);
    this.handleBreakpoints = this.handleBreakpoints.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.destroy = this.destroy.bind(this);

    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    if (!this.element) {
      return;
    }

    // Toggle 'is-pinned' class when element is "stuck".
    this.observer = new IntersectionObserver(([e]) => (
      e.target.classList.toggle('is-pinned', e.intersectionRatio < 1)
    ), { threshold: [1] });

    this.observer.observe(this.element);

    // Intent tracking to reveal site header on mobile when scrolling up.
    this.lastY = window.scrollY;
    this.upIntent = 0;
    this.downIntent = 0;
    this.THRESHOLD = 100; // px

    this.onScroll = this.handleScroll;

    // This should only trigger on smaller screens.
    this.handleBreakpoints();
    breakpoints.lgMax.addListener(this.handleBreakpoints);
  }

  /**
   * Attach/remove scroll listener based on lgMax breakpoint.
   */
  handleBreakpoints() {
    if (!this.element) {
      return;
    }

    if (breakpoints.lgMax.matches) {
      if (!this.scrollAttached) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.scrollAttached = true;
      }
    } else {
      if (this.scrollAttached) {
        window.removeEventListener('scroll', this.onScroll);
        this.scrollAttached = false;
      }
      // Reset state/classes when disabling behavior on larger screens.
      this.element.classList.remove('scroll-up', 'scroll-down');
      this.upIntent = 0;
      this.downIntent = 0;
      this.lastY = window.scrollY;
    }
  }

  /**
   * Handle scroll event.
   */
  handleScroll() {
    const currentY = window.scrollY;
    const delta = currentY - this.lastY;

    // Only act when header is pinned (sticky).
    if (this.element.classList.contains('is-pinned')) {
      if (delta > 0) {
        // scrolling down
        this.downIntent += delta;
        this.upIntent = 0;

        if (this.downIntent > this.THRESHOLD) {
          this.element.classList.add('scroll-down');
          this.element.classList.remove('scroll-up');
        }
      } else if (delta < 0) {
        // scrolling up
        this.upIntent -= delta; // delta is negative
        this.downIntent = 0;

        if (this.upIntent > this.THRESHOLD) {
          this.element.classList.add('scroll-up');
          this.element.classList.remove('scroll-down');
        }
      }
    }

    this.lastY = currentY;
  }

  /**
   * Clean up.
   */
  destroy() {
    if (this.scrollAttached) {
      window.removeEventListener('scroll', this.onScroll);
      this.scrollAttached = false;
    }

    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Remove breakpoint listener
    if (breakpoints?.lgMax?.removeListener) {
      breakpoints.lgMax.removeListener(this.handleBreakpoints);
    }

    this.onScroll = null;
  }
}
